import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import PostItem from '../components/PostItem';
import { getPost, searchPosts } from '../services/PostService';
import { getValidAccessTokenOrRefresh } from '../utils/AuthTokenUtil';

interface PostDetailPageProps {
  isAuthenticated: boolean;
  currentUserNickname?: string;
  onEdit: (postId: number) => void;
  onDelete: (post: any) => void;
  onRequireLogin: () => void;
}

const PostDetailPage: React.FC<PostDetailPageProps> = ({
  isAuthenticated,
  currentUserNickname,
  onEdit,
  onDelete,
  onRequireLogin,
}) => {
  const { postId } = useParams();
  const [post, setPost] = useState<any | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchPost = async () => {
      if (!postId) {
        setError('게시글을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const token = isAuthenticated ? await getValidAccessTokenOrRefresh() : null;
        const data = await getPost(postId, token);
        if (!cancelled) {
          setPost(data);
          if (data.category?.id) {
            const related = await searchPosts('', data.category.id, token);
            if (!cancelled) {
              setRelatedPosts(related.filter((item: any) => item.id !== data.id).slice(0, 5));
            }
          } else {
            setRelatedPosts([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setPost(null);
          setRelatedPosts([]);
          setError('게시글을 찾을 수 없거나 로그인이 필요합니다.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPost();

    return () => {
      cancelled = true;
    };
  }, [postId, isAuthenticated]);

  if (loading) {
    return <div className="empty-state">게시글을 불러오는 중입니다.</div>;
  }

  if (!post) {
    return (
      <div className="empty-state">
        <p>{error || '게시글을 찾을 수 없습니다.'}</p>
        {!isAuthenticated && (
          <Button variant="dark" size="sm" onClick={onRequireLogin}>
            로그인
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="post-list">
      <PostItem
        post={post}
        isAuthenticated={isAuthenticated}
        currentUserNickname={currentUserNickname}
        showUrlCopyButton
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {relatedPosts.length > 0 && (
        <section className="related-posts">
          <div className="related-posts-head">
            <strong>같은 카테고리 글</strong>
            {post.category?.name && <span>{post.category.name}</span>}
          </div>
          <div className="related-posts-list">
            {relatedPosts.map((relatedPost) => (
              <Link key={relatedPost.id} to={`/posts/${relatedPost.id}`} className="related-post-link">
                <span>{relatedPost.title}</span>
                <small>{new Date(relatedPost.createdAt).toLocaleDateString()}</small>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default PostDetailPage;
